import EditItemDialog from 'components/util/EditItemDialog';
import { Form, FormikProvider, useFormik } from 'formik';
import { BrandedLink } from '../../../@types/brandedLink';
import * as Yup from 'yup';
import { addBrandedLink, updateBrandedLink } from 'redux/slices/brandedLink';
import useGuardedDispatch from 'guards/useGuardedDispatch';
import { useRegisterUnsavedFormChanges } from 'components/form/useRegisterUnsavedFormChanges';
import { useTranslator } from 'translation/useTranslator';
import { TextField } from '@nps.today/component-library';
import { rules } from 'access';

const componentDataTestId = 'add-branded-link-dialog';

const BRANDED_LINK_URL_REGEX = /^https:\/\/[^\s?#]+$/;

type AddBrandedLinkDialogProps = {
  display: boolean;
  close: () => void;
  fetchBrandedLinks: () => void;
  brandedLinkToEdit?: BrandedLink;
};

const AddBrandedLinkDialog = ({
  display,
  close,
  brandedLinkToEdit,
  fetchBrandedLinks
}: AddBrandedLinkDialogProps) => {
  const translator = useTranslator();
  const guardedDispatch = useGuardedDispatch();

  const schema = Yup.object().shape({
    name: Yup.string().max(100).required(translator.brandedLinkNameRequired()),
    url: Yup.string()
      .max(2048)
      .matches(BRANDED_LINK_URL_REGEX, translator.brandedLinkUrlInvalid())
      .required(translator.brandedLinkUrlRequired())
  });

  const formik = useFormik<BrandedLink>({
    enableReinitialize: true,
    initialValues: {
      name: brandedLinkToEdit?.name ?? '',
      url: brandedLinkToEdit?.url ?? ''
    },
    validationSchema: schema,
    onSubmit: async (values, { setSubmitting }) => {
      const onSuccess = () => {
        fetchBrandedLinks();
        onClose();
      };
      const onFinally = () => {
        setSubmitting(false);
      };

      setSubmitting(true);
      if (brandedLinkToEdit?.id) {
        guardedDispatch({
          action: updateBrandedLink,
          actionParameters: { ...brandedLinkToEdit, ...values },
          rule: rules.brandedLink.update,
          onSuccess: onSuccess,
          onFinally: onFinally
        });
      } else {
        guardedDispatch({
          action: addBrandedLink,
          actionParameters: values,
          rule: rules.brandedLink.create,
          onSuccess: onSuccess,
          onFinally: onFinally
        });
      }
    }
  });

  const {
    submitForm,
    resetForm,
    errors,
    touched,
    dirty,
    isSubmitting,
    handleSubmit,
    getFieldProps
  } = formik;

  useRegisterUnsavedFormChanges(dirty);

  const onClose = () => {
    close();
    resetForm();
  };

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" onSubmit={handleSubmit}>
        <EditItemDialog
          itemType={translator.brandedLink().toLowerCase()}
          editable
          isOpen={display}
          title={
            brandedLinkToEdit ? translator.editBrandedLink() : translator.addNewBrandedLink()
          }
          editContent={[
            <TextField
              dataTestId={`${componentDataTestId}-name`}
              key="name"
              fullWidth
              label={translator.brandedLinkName()}
              {...getFieldProps('name')}
              required
              error={Boolean(touched.name && errors.name)}
              helperText={touched.name && errors.name}
            />,
            <TextField
              dataTestId={`${componentDataTestId}-url`}
              key="url"
              fullWidth
              label={translator.brandedLinkUrl()}
              placeholder="https://survey.acme.com"
              {...getFieldProps('url')}
              required
              error={Boolean(touched.url && errors.url)}
              helperText={touched.url && errors.url}
            />
          ]}
          dirty={dirty}
          isSubmitting={isSubmitting}
          close={() => onClose()}
          resetForm={resetForm}
          submitForm={submitForm}
        />
      </Form>
    </FormikProvider>
  );
};

export default AddBrandedLinkDialog;
